
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { themes, Head, Footer, Header, Steps, Notes, Invert, Split } from 'mdx-deck'
import './styles.css'
import { Motivation } from './10-files/Motivation'
import { PropTypesApp } from './10-files/PropTypes'
export const theme = { ...themes.highlight
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  theme
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    {
      /* general markup for all slides */
    }
    <Head mdxType="Head">
  <title>SofaScore Academy 10</title>
    </Head>
    <Header mdxType="Header">
  <div className="tC m8">SofaScore Academy 2020.</div>
    </Header>
    <Footer mdxType="Footer">
  <div className="tR m8">Lesson 10 - Type Checking</div>
    </Footer>
    {
      /* Slides */
    }
    <h1>{`Type Checking`}</h1>
    <h2>{`Lesson 10`}</h2>
    <hr></hr>
    <h1>{`Motivation`}</h1>
    <ul>
      <li parentName="ul">{`More robust application`}<ul parentName="li">
          <li parentName="ul">{`Static testing`}</li>
        </ul></li>
      <li parentName="ul">{`Better IDE support`}</li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Apps that include some form of type checking are usually more robust as they catch some type related bugs before deployment.
Type checking is part of static testing and as any test in provides developers with bigger confidence in their code.`}</li>
        <li parentName="ul">{`Better IDE support. Types are used by IDEs to get better context and type, so IDE can make better suggestions to developers because it can recognize types.`}</li>
      </ul>
    </Notes>
    <hr></hr>

    <Invert mdxType="Invert">
      <h1>{`Motivation - Example`}</h1>
      <Motivation mdxType="Motivation" />
    </Invert>
    <hr></hr>
    <h1>{`Statically typed vs Dynamically typed`}</h1>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Statically`}</strong>{` typed languages`}<ul parentName="li">
          <li parentName="ul">{`Type checking is done at `}<strong parentName="li">{`compile time`}</strong></li>
          <li parentName="ul">{`Errors are reported on compilation`}</li>
          <li parentName="ul">{`Java, C, TypeScript`}</li>
        </ul></li>
      <li parentName="ul"><strong parentName="li">{`Dynamically`}</strong>{` typed languages`}<ul parentName="li">
          <li parentName="ul">{`Type checking is done at `}<strong parentName="li">{`runtime`}</strong></li>
          <li parentName="ul">{`Errors are reported at runtime`}</li>
          <li parentName="ul">{`PHP, Python, JavaScript`}</li>
        </ul></li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Statically typed languages check types as part of their compile step, so any type related errors will be caught at compile time.
This ensures that type related bugs will be caught before deployment to the production.
Downside is need to specify type for each used variable and data structure. Note that TypeScript is statically typed.`}</li>
        <li parentName="ul">{`Dynamically typed languages check types at runtime (type is associated with variable value not with variable type).
Runtime checking means that type errors can only be caught on runtime, often in production by users, which is not good.
Upside of dynamically typed languages is simpler code as types are inferred from values not from the definitions.`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <h1>{`PropTypes`}</h1>
    <ul>
      <li parentName="ul">{`React specific type checker`}</li>
      <li parentName="ul">{`Provides validators for type checking props of React component`}</li>
      <li parentName="ul">{`Checking is done in runtime`}<ul parentName="li">
          <li parentName="ul">{`Error in console if React is in dev mode`}</li>
        </ul></li>
      <li parentName="ul">{`Used before as there weren't many alternatives like today`}</li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`PropTypes package works only with React apps.
Previously package was part of `}<inlineCode parentName="li">{`react`}</inlineCode>{` package but it was moved to separate package to make `}<inlineCode parentName="li">{`react`}</inlineCode>{` package smaller.`}</li>
        <li parentName="ul">{`Package provides type definitions which can be used to define component props
(e.g. name prop is optional, but must be string if provided, value prop is required and must be number)`}</li>
        <li parentName="ul">{`Potential errors will be reported in the runtime and written in console if React is in dev mode (it is when start script used).`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "https://reactjs.org/docs/typechecking-with-proptypes.html#proptypes"
          }}>{`Full list of validators`}</a></li>
      </ul>
    </Notes>
    <hr></hr>

    <Invert mdxType="Invert">
      <h1>{`PropTypes - Example`}</h1>
      <PropTypesApp mdxType="PropTypesApp" />
    </Invert>
    <hr></hr>
    <h1>{`Flow`}</h1>
    <ul>
      <li parentName="ul">{`Static type checker for JavaScript`}</li>
      <li parentName="ul">{`Compiler checks types and removes flow to produce valid JavaScript`}</li>
      <li parentName="ul">{`Can infer types from the usage`}</li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Flow can be integrated in any JS project, it is not React specific. Developers first have to install flow compiler (transpiler) and setup flow (`}<inlineCode parentName="li">{`.flowconfig`}</inlineCode>{`).
Flow will check only files with `}<inlineCode parentName="li">{`// @flow`}</inlineCode>{` comment. Compiler will then check types and report errors (static).
Compiler should also remove flow annotations, because flow isn't valid JavaScript.`}</li>
        <li parentName="ul">{`Flow can generate types from usage (e.g. for variable `}<inlineCode parentName="li">{`n`}</inlineCode>{` defined as `}<inlineCode parentName="li">{`const n = 3`}</inlineCode>{`, flow will automatically add number type).
This feature makes adding types easier, as many variables can be type checked without any extra work.`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <Invert mdxType="Invert">
      <h1>{`Flow - Example`}</h1>
    </Invert>
    <hr></hr>
    <h1>{`TypeScript`}</h1>
    <ul>
      <li parentName="ul">{`Typed superset of JavaScript that compiles to plain JavaScript`}</li>
      <li parentName="ul">{`Includes compiler `}<inlineCode parentName="li">{`tsc`}</inlineCode></li>
      <li parentName="ul">{`Infers types from the usage`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`*.ts`}</inlineCode>{` or `}<inlineCode parentName="li">{`*.tsx`}</inlineCode>{` extensions.`}<ul parentName="li">
          <li parentName="ul">{`React will not work in `}<inlineCode parentName="li">{`.ts`}</inlineCode>{` files`}</li>
        </ul></li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Typescript is programming language, superset of JavaScript that compiles to JavaScript.
TypeScript can understand any valid JavaScript file, so it can be adopted gradually (`}<inlineCode parentName="li">{`allowJs`}</inlineCode>{` compiler flag).`}</li>
        <li parentName="ul">{`TypeScript package includes compiler which can be ran with default settings or specific settings can be provided in `}<inlineCode parentName="li">{`tsconfig.json`}</inlineCode>{` file in project root
(Compiler will use it automatically if file exists). Compiler can be modified with settings. `}<a parentName="li" {...{
            "href": "https://www.typescriptlang.org/docs/handbook/compiler-options.html"
          }}>{`List of all compiler settings`}</a></li>
        <li parentName="ul">{`Similar to Flow, TypeScript can infer types from usage and developers should avoid explicit type definitions where compiler can deduce correct type
(code will be minimal, type checking will be better, refactors will be easier).`}</li>
        <li parentName="ul">{`Typescript files have `}<inlineCode parentName="li">{`.ts`}</inlineCode>{` extension if they don't use JSX. If JSX is used (e.g. with React),
file should have `}<inlineCode parentName="li">{`.tsx`}</inlineCode>{` extension because TypeScript will report `}<inlineCode parentName="li">{`<`}</inlineCode>{` and `}<inlineCode parentName="li">{`>`}</inlineCode>{` as errors in ordinary TypeScript files.`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <h1>{`TS - Basics`}</h1>
    <ul>
      <li parentName="ul">{`Base types:`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`number`}</inlineCode>{`, `}<inlineCode parentName="li">{`boolean`}</inlineCode>{`, `}<inlineCode parentName="li">{`string`}</inlineCode>{`, `}<inlineCode parentName="li">{`null`}</inlineCode>{`, `}<inlineCode parentName="li">{`undefined`}</inlineCode>{`, `}<inlineCode parentName="li">{`Object`}</inlineCode>{`, `}<inlineCode parentName="li">{`Array`}</inlineCode>{`, `}<inlineCode parentName="li">{`void`}</inlineCode>{`, `}<inlineCode parentName="li">{`any`}</inlineCode></li>
          <li parentName="ul">{`Functions are defined with arguments and return type`}</li>
        </ul></li>
      <li parentName="ul">{`Interface`}<ul parentName="li">
          <li parentName="ul">{`Provide shape of some values`}</li>
          <li parentName="ul">{`Used to name complex object shapes`}</li>
          <li parentName="ul">{`Can extend other interfaces`}</li>
        </ul></li>
      <li parentName="ul">{`Type`}<ul parentName="li">
          <li parentName="ul">{`Create new TS type`}</li>
          <li parentName="ul">{`Can name primitives`}</li>
        </ul></li>
      <li parentName="ul">{`Enum`}<ul parentName="li">
          <li parentName="ul">{`Set of named constants`}</li>
          <li parentName="ul">{`String or number values`}</li>
        </ul></li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Base types include:`}<ul parentName="li">
            <li parentName="ul"><inlineCode parentName="li">{`number`}</inlineCode>{`, `}<inlineCode parentName="li">{`boolean`}</inlineCode>{`, `}<inlineCode parentName="li">{`string`}</inlineCode>{`, `}<inlineCode parentName="li">{`null`}</inlineCode>{`, `}<inlineCode parentName="li">{`undefined`}</inlineCode>{`, `}<inlineCode parentName="li">{`Object`}</inlineCode>{`, `}<inlineCode parentName="li">{`Array`}</inlineCode>{`, `}<inlineCode parentName="li">{`void`}</inlineCode>{`, `}<inlineCode parentName="li">{`any`}</inlineCode>{` and `}<inlineCode parentName="li">{`never`}</inlineCode>{`.
Most of types are straight forward. `}<inlineCode parentName="li">{`void`}</inlineCode>{` is used as return value for functions that don't return.
`}<inlineCode parentName="li">{`any`}</inlineCode>{` is used for variables without type definitions and marks any possible type (avoid whenever possible).
`}<inlineCode parentName="li">{`never`}</inlineCode>{` is rarely used explicitly but can appear implicitly. It marks return type of a function that never returns
(e.g. throws error before return, runs infinite loop in function body, etc.)`}</li>
          </ul></li>
        <li parentName="ul">{`Interface`}<ul parentName="li">
            <li parentName="ul">{`Name object shape (e.g. React component props). Interfaces can extend other interfaces or be implemented by classes.`}</li>
            <li parentName="ul">{`Interface value (shape) will always be aliased with it's name (e.g. editor will show that some function takes interface by name, it will not tell the shape of the interface)`}</li>
          </ul></li>
        <li parentName="ul">{`Type`}<ul parentName="li">
            <li parentName="ul">{`Defines new TS type. Similar to interface, but can be used to define primitive or union types (interface can't). Cannot be extended.`}</li>
            <li parentName="ul">{`Type value will not be aliased so editor will show that some function takes exact properties and types, not the type name.`}</li>
          </ul></li>
        <li parentName="ul">{`Enum`}<ul parentName="li">
            <li parentName="ul">{`Set of named constants. TS can define number and string enums. Number enums are default, incrementation starts from 0, so values can sometimes be omitted.`}</li>
          </ul></li>
      </ul>
    </Notes>
    <hr></hr>
    <Invert mdxType="Invert">
      <h1>{`Basic TypeScript - Example`}</h1>
    </Invert>
    <hr></hr>
    <h1>{`TS - Dependency types`}</h1>
    <ul>
      <li parentName="ul">{`Some 3rd party libraries provide types for the API with the library`}</li>
      <li parentName="ul">{`Other libraries provide types as separate package`}<ul parentName="li">
          <li parentName="ul">{`Those types are published as DefinitelyTyped packages`}</li>
          <li parentName="ul">{`Name is `}<inlineCode parentName="li">{`@types/nameOfPackage`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Declaration files (`}<inlineCode parentName="li">{`*.d.ts`}</inlineCode>{`)`}<ul parentName="li">
          <li parentName="ul">{`Provide type declarations and definitions for libraries without types`}</li>
          <li parentName="ul">{`TS will treat libraries without types as errors, so they should be at least declared.`}</li>
        </ul></li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Some 3rd party libraries provide types for the API with the library.
Those libraries can be seamlesly integrated and TS will use their type definitions when compiling.`}</li>
        <li parentName="ul">{`Other libraries provide types as separate package. Those types are published as `}<a parentName="li" {...{
            "href": "https://github.com/DefinitelyTyped/DefinitelyTyped"
          }}>{`DefinitelyTyped`}</a>{` packages.
Packages are named with `}<inlineCode parentName="li">{`@types/*`}</inlineCode>{` prefix.`}</li>
        <li parentName="ul">{`If some library doesn't provide types, types should be defined via `}<inlineCode parentName="li">{`*.d.ts`}</inlineCode>{` files. Those files server as decalaration files with types.
Developers can choose to not define any API-s (everything will be `}<inlineCode parentName="li">{`any`}</inlineCode>{`) or to write more specific styles. Only rule is that there has to be declaration.
Declaration files can be written for external libraries, when exposing own library to others or for specific modules (rarely).`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <h1>{`TS - Utilities`}</h1>
    <ul>
      <li parentName="ul">{`TypeScript provides some utilities to solve common problems`}</li>
      <li parentName="ul">{`Generics - Provide more reusability`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`function usePlusOperator<T = number>(a: T, b: T) => a + b`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Record - Define generalized objects (maps)`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Record<string, number>`}</inlineCode>{` - object where all keys are `}<inlineCode parentName="li">{`string`}</inlineCode>{` and values `}<inlineCode parentName="li">{`number`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Pick - Type from parts of some interface`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Pick<Props, 'value' | 'label'>`}</inlineCode>{` - only definitions for `}<inlineCode parentName="li">{`value`}</inlineCode>{` and `}<inlineCode parentName="li">{`label`}</inlineCode>{` from `}<inlineCode parentName="li">{`Props`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Omit - Type from all definitions of an interface except those specified`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Omit<Props, 'value' | 'label'>`}</inlineCode>{` - all definitions from `}<inlineCode parentName="li">{`Props`}</inlineCode>{` except `}<inlineCode parentName="li">{`value`}</inlineCode>{` and `}<inlineCode parentName="li">{`label`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Partial - Make whole interface optional`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Partial<Props>`}</inlineCode>{` - all definitions in `}<inlineCode parentName="li">{`Props`}</inlineCode>{` are optional`}</li>
        </ul></li>
      <li parentName="ul">{`Casting - Change type (make more specific)`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`const a: string | number; const b = a as string`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Ignoring - Ignore next line while compiling
`}<inlineCode parentName="li">{`// @ts-ignore`}</inlineCode>{` - use sparingly`}</li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`This slide is more informative, some of these utilities will be helpful some will be rarely used.`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <h1>{`TS - React`}</h1>
    <ul>
      <li parentName="ul">{`React included types and expose commonly used interfaces`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`React.ReactNode`}</inlineCode>{` - Interface defining any valid React child (e.g. type definition for `}<inlineCode parentName="li">{`children`}</inlineCode>{` prop)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`React.Component<Props, State>`}</inlineCode>{` - Generic for defining props and state types for component`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`create-react-app my-app-name --template typescript`}</inlineCode>{` - Generates CRA with TypeScript included`}</li>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "https://create-react-app.dev/docs/adding-typescript/"
          }}>{`CRA with TS`}</a>{`.`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <Invert mdxType="Invert">
      <h1>{`TypeScript App - Example`}</h1>
    </Invert>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;